import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import BackflowFormImageSection from './BackflowFormImageSection';
import BackflowSelectDropdown from './BackflowSelectDropdown';
import DeleteConfirmationModal from '../../../sharedModules/layout/DeleteConfirmationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import RadioGroup from '../../../sharedModules/layout/RadioGroup/RadioGroup';

import { BackflowLog, BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

import { backflowSizeOptions, yesNoOptions, passFailOptions } from '../constants/formOptions';

interface BackflowLogFormFullProps {
  backflowJobItem: BackflowJobItem;
  formValues: BackflowLog;
  setFormValues: Dispatch<SetStateAction<BackflowLog>>;
  onRequiredImagesValidityChange?: (deviceImageAttached: boolean, dataplateAttached: boolean) => void;
  showRequiredImageErrors?: boolean;
}

const BackflowLogFormFull: React.FC<BackflowLogFormFullProps> = (props) => {
  const { id } = useParams<{ id?: string }>();

  const { backflowJobItem, formValues, setFormValues, onRequiredImagesValidityChange, showRequiredImageErrors } = props;

  const [backflowImageAlbum, setBackflowImageAlbum] = useState<any>();
  const [cannotCompleteLineTest, setCannotCompleteLineTest] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(Boolean(id));

  const fetchBackflowImageAlbum = async () => {
    setLoading(true);
    try {
      const response = await axios.get<string, any>(`/backflows/backflow_log_images/${id}`);
      setBackflowImageAlbum(response.data.result);
      setErrorMessage('');
    } catch (err: any) {
      setErrorMessage('Something went wrong fetching the Images');
    } finally {
      setLoading(false);
    }
  };

  const updateBackflowLog = async (updatedFormValues: BackflowLog) => {
    try {
      await axios.put(`/backflows/backflow_logs/${backflowJobItem.backflow_log_id}`, {
        backflow_log: updatedFormValues,
      });
    } catch (err) {
      setErrorMessage('Something went wrong updating the Backflow Log');
    }
  };

  const handleFormSelectionChange = (fieldName: string, value: string) => {
    const updatedFormValues = {
      ...formValues,
      [fieldName]: value,
    };
    setFormValues(updatedFormValues);
    updateBackflowLog(updatedFormValues);
  };

  useEffect(() => {
    if (id) fetchBackflowImageAlbum();
  }, [id]);

  useEffect(() => {
    let v = formValues?.line_pressure_psi;
    if (!v) return;
    setCannotCompleteLineTest(v < 0.1);
  }, [formValues?.line_pressure_psi]);

  useEffect(() => {
    const deviceImages = backflowImageAlbum?.backflow_device_images ?? [];
    const dataplateImages = backflowImageAlbum?.capture_backflow_dataplates ?? [];
    const deviceImageAttached = deviceImages.length > 0;
    const dataplateAttached = dataplateImages.length > 0;
    onRequiredImagesValidityChange?.(deviceImageAttached, dataplateAttached);
  }, [backflowImageAlbum, onRequiredImagesValidityChange]);

  const handleInputChange = (field: keyof BackflowLog, value: boolean | string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLinePressureChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCannotCompleteLineTest(checked);

    if (!checked) return;
    setFormValues((prev) => ({
      ...prev,
      line_pressure_psi: 0,
    }));
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Card>
        <CardContent>
          <Grid container>
            <DeleteConfirmationModal afterDelete={fetchBackflowImageAlbum} />
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage} />
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="backflow_device_images"
                label="Backflow Device Image"
                required
                error={showRequiredImageErrors && !(backflowImageAlbum?.backflow_device_images ?? []).length}
              />
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="capture_backflow_dataplates"
                label="Capture Backflow Dataplate"
                required
                error={showRequiredImageErrors && !(backflowImageAlbum?.capture_backflow_dataplates ?? []).length}
              />
            </Grid>
            <Grid item xs={12}>
              <BackflowSelectDropdown
                value={formValues?.backflow_size}
                label="Backflow Size"
                onChange={(value) => handleFormSelectionChange('backflow_size', value)}
                options={backflowSizeOptions}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Line Pressure PSI (Measurement)"
                type="number"
                value={formValues.line_pressure_psi}
                onChange={(e) => handleInputChange('line_pressure_psi', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">PSI</InputAdornment> }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                disabled={cannotCompleteLineTest}
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: -1.5, pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox checked={cannotCompleteLineTest} onChange={handleLinePressureChecked} color="primary" />
                }
                label="Line Pressure Test Cannot be Performed"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                Test Procedures
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                {backflowJobItem.backflow_type === 'Backflow Preventer'
                  ? 'Test #1 - Tightness of the #1 Shutoff Valve'
                  : 'Tightness of #1 Check Valve'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="test1_documentations"
                label="Test #1"
              />
            </Grid>
            <Grid item xs={12} md={8} style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
              <RadioGroup
                label="#1 Check valve tight"
                radioOptions={yesNoOptions}
                value={formValues.test1_valve_tight}
                setValue={(val) => handleFormSelectionChange('test1_valve_tight', val)}
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <TextField
                label="#1 Gauge value (Measurement)"
                type="number"
                value={formValues.test1_gauge_value}
                onChange={(e) => handleInputChange('test1_gauge_value', e.target.value)}
                InputProps={{
                  endAdornment: 'PSI',
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                {backflowJobItem.backflow_type === 'Backflow Preventer'
                  ? 'Test #2 - Tightness of the #2 Shutoff Valve'
                  : 'Tightness of #2 Check Valve'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="test2_documentations"
                label="Test #2"
              />
            </Grid>
            <Grid item xs={12} md={8} style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
              <RadioGroup
                label="#2 Check valve tight"
                radioOptions={yesNoOptions}
                value={formValues.test2_valve_tight}
                setValue={(val) => handleFormSelectionChange('test2_valve_tight', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="#2 Gauge value (Measurement)"
                type="number"
                value={formValues.test2_gauge_value}
                onChange={(e) => handleInputChange('test2_gauge_value', e.target.value)}
                InputProps={{
                  endAdornment: 'PSI',
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                sx={{ mb: 3 }}
              />
            </Grid>
            {backflowJobItem.backflow_type === 'Backflow Preventer' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Test #3 - Relief valve opening
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8} style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                  <RadioGroup
                    label="#3 Did Valve Open?"
                    radioOptions={yesNoOptions}
                    value={formValues.test3_valve_open}
                    setValue={(val) => handleFormSelectionChange('test3_valve_open', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Relief valve opening pressure (Measurement)"
                    type="number"
                    value={formValues.test3_relief_valve_pressure}
                    onChange={(e) => handleInputChange('test3_relief_valve_pressure', e.target.value)}
                    InputProps={{
                      endAdornment: 'PSI',
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                System Restoration & Results
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="restore_system_documentations"
                label="Restore System"
              />
            </Grid>
            <Grid item xs={12} md={8} style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
              <RadioGroup
                label="Pass or Fail"
                radioOptions={passFailOptions}
                value={formValues.pass_or_fail}
                setValue={(val) => handleFormSelectionChange('pass_or_fail', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <BackflowFormImageSection
                backflowImageAlbum={backflowImageAlbum}
                fetchBackflowImageAlbum={fetchBackflowImageAlbum}
                category="customer_documents"
                label="Customer Documents"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                rows={5}
                multiline
                label="Internal Comment (Optional)"
                value={formValues.internal_comment}
                onChange={(e) => setFormValues({ ...formValues, internal_comment: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 3 }}
              />
            </Grid>
            {/* Hidden for now, but the field is ready in the backend to accept input. */}
            {/* <Grid item xs={12}>
              <TextField
                rows={5}
                multiline
                label="Client Comment (Optional)"
                value={formValues.client_comment}
                onChange={(e) => setFormValues({ ...formValues, client_comment: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 3 }}
              />
            </Grid> */}
          </Grid>
        </CardContent>
      </Card>
    </LoadingWrapper>
  );
};

export default BackflowLogFormFull;
